import type { Task } from '@dyspatch/core';
import { taskTone } from '@/components/common/StatusIcon';
import { countedTasks } from '@/lib/briefs';
import { compareKeys } from '@/lib/graph';
import { cn } from '@/lib/utils';

/** A job with more steps than this is past the twenty the design plans for. */
const MAX_DOTS = 20;

/**
 * A job's steps as a row of dots, coloured by state (docs/JOB-LIFECYCLE.md 2.8).
 *
 * It sits next to the "3 of 7 done" bar and says the part the fraction cannot:
 * *which* three, and what the other four are doing. A page of jobs then reads as
 * a page of shapes - four greens and a gate indigo is a different job from four
 * greys, and both say "1 of 5" in words.
 *
 * It draws exactly the steps the fraction counts (`countedTasks`), so the dots
 * and the number beside them are the same sentence twice and cannot disagree.
 * The hue comes from `taskTone`, the same table the status icon uses, so a dot
 * and an icon for one state are never two different colours.
 *
 * Shape carries nothing here, so the label carries it instead: the row is one
 * `role="img"` with a count per state rather than twenty separate labels.
 */
export function TaskDots({ tasks, className }: { tasks: readonly Task[]; className?: string }) {
  const counted = countedTasks(tasks).sort((a, b) => compareKeys(a.key, b.key));
  if (!counted.length) return null;

  const byStatus = new Map<string, number>();
  for (const task of counted) byStatus.set(task.status, (byStatus.get(task.status) ?? 0) + 1);
  const label = [...byStatus.entries()].map(([status, count]) => `${count} ${status}`).join(', ');
  const shown = counted.slice(0, MAX_DOTS);
  const rest = counted.length - shown.length;

  return (
    <span
      className={cn('flex min-w-0 items-center gap-[3px]', className)}
      role="img"
      aria-label={`Tasks: ${label}`}
    >
      {shown.map((task) => (
        <span
          key={task.id}
          title={`${task.key} · ${task.status}`}
          className={cn('size-[6px] shrink-0 rounded-full bg-current', taskTone(task.status))}
        />
      ))}
      {rest > 0 ? <span className="text-fg-3 tnum shrink-0 text-2xs">+{rest}</span> : null}
    </span>
  );
}
